import { Alert, AlertTitle, Button, Dialog, DialogActions, DialogContent, DialogTitle, Box, TextField, Autocomplete, Select, MenuItem, FormControl, InputLabel, Link, Typography, Checkbox } from "@mui/material";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { RealInput } from "../../../core/components/RealInput";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { MetodoPagamento } from "../../../core/enum/MetodoPagamento";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { useConfiguracoesPedidoContext } from "../../contexts/ConfiguracoesPedidoContext";
import { IPagamentoPedido } from "../../interface/IPagamentoPedido";
import { MetodoPagamentoItem, MetodoPagamentoIconBox } from "./styles";
import { Pix, Payments, CreditCard, CheckCircle } from "@mui/icons-material";
import { useThemeContext } from "../../../core/contexts/ThemeContext";

interface MetodoPagamentoSelectProps {
    pagamento: IPagamentoPedido | undefined;
    onChange?: (pagamentos?: IPagamentoPedido) => void;
    valorTotal?: number;
    size?: "small" | "medium"
    disabled?: boolean;
}

export const MetodoPagamentoSelect = ({ onChange, pagamento, valorTotal, size, disabled }: MetodoPagamentoSelectProps) => {
    const [openTroco, setOpenTroco] = useState(false);
    const { configuracao } = useConfiguracoesPedidoContext();
    const { real } = useMoeda();
    const snackbar = useSnackbar();
    const { themeName } = useThemeContext();

    const [valorRecebido, setValorRecebido] = useState<undefined | number>();
    const valorTroco = useMemo(() => valorRecebido ? (valorRecebido - (valorTotal ?? 0)) : 0, [valorTotal, valorRecebido, pagamento])

    const getIconAndType = (metodo: string) => {
        if (metodo === MetodoPagamento.Pix) {
            return { icon: Pix, type: 'pix' as const };
        }
        if (metodo === MetodoPagamento.Dinheiro) {
            return { icon: Payments, type: 'dinheiro' as const };
        }
        return { icon: CreditCard, type: 'cartao' as const };
    }

    const handleClose = () => {
        setOpenTroco(false);
        setValorRecebido(undefined);
    }

    useEffect(() => {
        if (pagamento) {
            setValorRecebido(pagamento.valorRecebido);
        }
    }, [pagamento])

    const handleOpen = () => setOpenTroco(true);

    const handleMetodoPagamento = (metodoPagamento: string | null) => {
        // Se o método clicado já está selecionado, desmarcar
        if (metodoPagamento && metodoPagamento === pagamento?.metodoPagamento) {
            if (onChange) {
                return onChange(undefined);
            }
            return;
        }

        // Se for Dinheiro e não estiver desmarcando, abrir dialog
        if (metodoPagamento == MetodoPagamento.Dinheiro) {
            handleOpen();
        }

        if (onChange) {
            return onChange({
                metodoPagamento: metodoPagamento ?? undefined,
                valorRecebido,
                valorTroco,
                valor: valorTotal
            });
        }
    }

    const handleMetodoPagamentoDinheiro = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (valorTroco && valorTroco < 0) {
            return snackbar("Troco inválido", { severity: "error" });
        }

        if (onChange) {
            onChange({
                metodoPagamento: MetodoPagamento.Dinheiro,
                valorRecebido,
                valorTroco,
                valor: valorTotal
            });
        }
        handleClose();
    }

    const handleValorRecebido = (e: ChangeEvent<HTMLInputElement>) => {
        setValorRecebido(Math.ceil(parseFloat(e.target.value)));
    }

    const isPaperMode = configuracao?.metodosPagamento && configuracao.metodosPagamento.length <= 4;

    return (
        <Box sx={{ width: '100%', marginTop: '1rem' }}>
            <Dialog open={openTroco} onClose={handleClose}>
                <form onSubmit={handleMetodoPagamentoDinheiro}>
                    <DialogTitle>Calcular troco</DialogTitle>
                    <DialogContent sx={{ width: 400 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Valor recebido"
                            type="text"
                            fullWidth
                            onChange={handleValorRecebido}
                            value={valorRecebido}
                            InputProps={{
                                inputComponent: RealInput as any,
                            }}
                        />
                        <Alert severity="info" sx={{ marginTop: "2rem" }}>
                            <AlertTitle>
                                {real(valorTotal)}
                            </AlertTitle>
                            Valor total
                        </Alert>
                        {valorTroco && valorTroco < 0 ? (
                            <Alert severity="error" sx={{ marginTop: "2rem" }}>
                                <AlertTitle>
                                    {real(valorTroco)}
                                </AlertTitle>
                                Troco inválido
                            </Alert>
                        ) : (
                            <Alert severity="success" sx={{ marginTop: "2rem" }}>
                                <AlertTitle>
                                    {real(valorTroco)}
                                </AlertTitle>
                                Troco válido
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button type="button" onClick={handleClose}>Cancelar</Button>
                        <Button type="submit">Salvar</Button>
                    </DialogActions>
                </form>
            </Dialog>
            <Typography
                sx={{
                    fontSize: 11,
                    color: 'grey.500',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '12px',
                    fontFamily: 'Poppins, sans-serif'
                }}
            >
                MÉTODO DE PAGAMENTO
            </Typography>
            {configuracao?.metodosPagamento && configuracao.metodosPagamento.length > 0 ? (
                isPaperMode ? (
                    <>
                        {configuracao.metodosPagamento.map((metodo, index) => {
                            const { icon: Icon, type } = getIconAndType(metodo);
                            const isSelecionado = metodo === pagamento?.metodoPagamento;

                            return (
                                <MetodoPagamentoItem
                                    key={metodo}
                                    selecionado={isSelecionado}
                                    onClick={() => handleMetodoPagamento(metodo)}
                                    elevation={isSelecionado ? 0 : 1}
                                    isFirst={index === 0}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                        <MetodoPagamentoIconBox tipoPagamento={type} themeName={themeName}>
                                            <Icon />
                                        </MetodoPagamentoIconBox>
                                        <Typography
                                            sx={{
                                                fontWeight: 500,
                                                fontSize: 14,
                                                fontFamily: 'Poppins, sans-serif'
                                            }}
                                        >
                                            {metodo}
                                        </Typography>
                                    </Box>
                                    {isSelecionado && (
                                        <CheckCircle
                                            sx={{
                                                color: 'primary.main',
                                                fontSize: '24px'
                                            }}
                                        />
                                    )}
                                </MetodoPagamentoItem>
                            );
                        })}
                    </>
                ) : (
                    <FormControl fullWidth>
                        <Autocomplete
                            value={pagamento?.metodoPagamento || ""}
                            onChange={(event, newValue) => {
                                handleMetodoPagamento(newValue);
                            }}
                            disabled={disabled}
                            options={configuracao?.metodosPagamento || []}
                            sx={{
                                '& .MuiAutocomplete-popper': {
                                    '& .MuiPaper-root': {
                                        borderRadius: '8px',
                                    },
                                },
                                '& .MuiAutocomplete-listbox': {
                                    '& .MuiAutocomplete-option': {
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="filled"
                                    size={size}
                                    disabled={disabled}
                                    sx={{
                                        '& .MuiFilledInput-root': {
                                            backgroundColor: 'white',
                                            borderRadius: '12px',
                                            boxShadow: 1,
                                            minHeight: size === 'small' ? '40px' : '56px',
                                            paddingTop: '0px !important',
                                            paddingBottom: '0px !important',
                                            '&:hover': {
                                                boxShadow: 2,
                                            },
                                            '&.Mui-focused': {
                                                boxShadow: 3,
                                            },
                                            '&::before': {
                                                display: 'none',
                                            },
                                            '&::after': {
                                                display: 'none',
                                            },
                                        },
                                        '& .MuiFilledInput-underline': {
                                            '&::before': {
                                                display: 'none',
                                            },
                                            '&::after': {
                                                display: 'none',
                                            },
                                        },
                                        '& .MuiInputBase-root': {
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                        '& .MuiInputBase-input': {
                                            textAlign: 'left',
                                        },
                                        '& .MuiFilledInput-input': {
                                            padding: size === 'small' ? '12px 12px' : '18px 12px',
                                            paddingTop: size === 'small' ? '12px' : '18px',
                                            paddingBottom: size === 'small' ? '12px' : '18px',
                                            lineHeight: '1.5',
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                        '& .MuiAutocomplete-endAdornment': {
                                            position: 'absolute',
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                    }}
                                />
                            )}
                        />
                    </FormControl>
                )
            ) : (
                <Alert severity="info">
                    Nenhum método de pagamento configurado. <br />
                    <Link href="/configuracoes/metodos-pagamento">Clique aqui</Link> para configurar.
                </Alert>
            )}
            {pagamento?.metodoPagamento === MetodoPagamento.Dinheiro && (
                <>
                    {valorTroco && valorTroco < 0 ? (
                        <Alert severity="error" sx={{ marginTop: "0.6rem" }}>
                            <AlertTitle>
                                {real(valorTroco)}
                            </AlertTitle>
                            Troco inválido
                        </Alert>
                    ) : (
                        <Alert severity="success" sx={{ marginTop: "0.6rem" }}>
                            <AlertTitle>
                                {real(valorTroco)}
                            </AlertTitle>
                            Troco válido
                        </Alert>
                    )}
                </>
            )}
        </Box>
    );
}
